import { useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { useIntl } from "react-intl";
import { useParams } from "react-router-dom";
import { useMutation, useQueryClient } from "@tanstack/react-query";

import { postQuoteRequestComment } from "api";
import { quoteRequestQueryKeys } from "api/queries/quotes/quoteRequestQueryKeys";
import { useGetQuoteRequestComments } from "api/queries/quotes/useGetQuoteRequestComments";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import CommonComments from "pages/common/ShipmentQuotesV2/components/Comments";
import errorToast from "helpers/errorToast";
import usePermissions from "hooks/usePermissions";
import useFileHook from "hooks/useFileHook";

function Comments() {
  const intl = useIntl();
  const queryClient = useQueryClient();
  const { id } = useParams();
  const shipmentId = id!;

  const { permissions } = usePermissions();
  const { company_id } = JSON.parse(permissions ?? "{}");

  const methods = useForm({
    defaultValues: {
      selected_message_audience: "",
      comment_message: "",
    },
  });
  const { getValues, setValue } = methods;

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { data: quoteComments, isInitialLoading: quoteCommentsLoading }: any = useGetQuoteRequestComments(shipmentId, {
    enabled: !!id,
  });

  const { data: shipmentData }: any = useGetShipmentBasic(shipmentId, {
    enabled: !!shipmentId,
    refetchOnMount: false,
  });

  useEffect(() => {
    if (getValues("selected_message_audience") === "") {
      setValue("selected_message_audience", shipmentData?.shipment?.shipper_id || "");
    }
  }, [shipmentData?.shipment?.shipper_id]);

  const audienceOptions = [
    {
      value: shipmentData?.shipment?.shipper_id,
      label: intl.formatMessage({ id: "general.shipper", defaultMessage: "Shipper" }),
      sublabel: intl.formatMessage({
        id: "quote-request.comments.shipper.sublabel",
        defaultMessage: "Direct message to shipper",
      }),
    },
    {
      value: company_id,
      label: intl.formatMessage({ id: "quote-request.comments.internalUsers", defaultMessage: "Internal users" }),
      sublabel: intl.formatMessage({
        id: "quote-request.comments.internalUsers.sublabel",
        defaultMessage: "Only people from your company",
      }),
    },
  ];

  const fileProps = useFileHook({ maxFileCount: 10 });
  const { resetFiles, formattedFiles } = fileProps;

  const { mutate, isLoading: isSubmitting } = useMutation({
    mutationFn: () => {
      const payload = {
        comment: textAreaRef.current?.value,
        audience: getValues("selected_message_audience"),
        ...(formattedFiles?.length > 0 && {
          attachment_references: formattedFiles.map((file) => file.reference_number),
        }),
      };
      return postQuoteRequestComment(id, payload);
    },
    onSuccess: () => {
      setValue("comment_message", "");
      textAreaRef.current!.value = "";
      queryClient.invalidateQueries(quoteRequestQueryKeys.quoteCommentsListing(shipmentId));
      resetFiles();
    },
    onError: (err) => {
      errorToast(err);
    },
  });

  return (
    <CommonComments
      methods={methods}
      audienceOptions={audienceOptions}
      textAreaRef={textAreaRef}
      onSubmitComment={mutate}
      isSubmitting={isSubmitting || quoteCommentsLoading}
      comments={quoteComments?.results}
      fileProps={fileProps}
    />
  );
}

export default Comments;

Comments.defaultProps = {
  isQuickRequest: false,
};
